import { useState, useEffect } from 'react';
import { Clock, Zap, TrendingUp } from 'lucide-react';

const CountdownTimer = ({ 
  targetDate, 
  label, 
  icon: Icon = Clock,
  colorClass = 'text-emerald-400',
  bgClass = 'bg-emerald-500/10',
  borderClass = 'border-emerald-500/30'
}) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const target = new Date(targetDate);
      const diff = target - now;

      if (diff <= 0) {
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const formatNumber = (num) => String(num).padStart(2, '0');

  if (isExpired) {
    return (
      <div className={`rounded-xl p-4 ${bgClass} border ${borderClass}`}>
        <div className="flex items-center gap-2 mb-2">
          <Icon className={`h-4 w-4 ${colorClass}`} />
          <span className="text-slate-400 text-sm font-medium">{label}</span>
        </div>
        <p className={`text-lg font-bold ${colorClass}`}>Deadline Passed</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl p-4 ${bgClass} border ${borderClass}`} data-testid={`countdown-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`h-4 w-4 ${colorClass}`} />
        <span className="text-slate-400 text-sm font-medium">{label}</span>
      </div>
      
      <div className="flex items-center gap-2">
        {timeLeft.days > 0 && (
          <>
            <div className="text-center">
              <div className={`text-2xl sm:text-3xl font-bold ${colorClass} font-mono`}>
                {formatNumber(timeLeft.days)}
              </div>
              <div className="text-xs text-slate-500 uppercase">Days</div>
            </div>
            <span className={`text-xl ${colorClass} opacity-50`}>:</span>
          </>
        )}
        
        <div className="text-center">
          <div className={`text-2xl sm:text-3xl font-bold ${colorClass} font-mono`}>
            {formatNumber(timeLeft.hours)}
          </div>
          <div className="text-xs text-slate-500 uppercase">Hrs</div>
        </div>
        <span className={`text-xl ${colorClass} opacity-50`}>:</span>
        
        <div className="text-center">
          <div className={`text-2xl sm:text-3xl font-bold ${colorClass} font-mono`}>
            {formatNumber(timeLeft.minutes)}
          </div>
          <div className="text-xs text-slate-500 uppercase">Min</div>
        </div>
        <span className={`text-xl ${colorClass} opacity-50`}>:</span>
        
        <div className="text-center">
          <div className={`text-2xl sm:text-3xl font-bold ${colorClass} font-mono`}>
            {formatNumber(timeLeft.seconds)}
          </div>
          <div className="text-xs text-slate-500 uppercase">Sec</div>
        </div>
      </div>
    </div>
  );
};

// Helper to get next occurrence of a specific day/time
export const getNextDeadline = (dayOfWeek, hour, minute = 0) => {
  const now = new Date();
  const target = new Date(now);
  
  // Set to target day of week (0 = Sunday, 5 = Friday)
  const daysUntilTarget = (dayOfWeek - now.getUTCDay() + 7) % 7;
  target.setUTCDate(now.getUTCDate() + daysUntilTarget);
  target.setUTCHours(hour, minute, 0, 0);
  
  // If the target time has passed this week, move to next week
  if (target <= now) {
    target.setUTCDate(target.getUTCDate() + 7);
  }
  
  return target;
};

// Pre-configured deadline helpers
export const getChipDeadline = () => getNextDeadline(0, 0); // Sunday 00:00 UTC
export const getTradingDeadline = () => getNextDeadline(5, 18); // Friday 18:00 UTC

// Dual countdown component for Dashboard
export const DeadlineCountdowns = () => {
  const chipDeadline = getChipDeadline();
  const tradingDeadline = getTradingDeadline();

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="deadline-countdowns">
      <CountdownTimer
        targetDate={tradingDeadline}
        label="Trading Window Closes"
        icon={TrendingUp}
        colorClass="text-blue-400"
        bgClass="bg-blue-500/10"
        borderClass="border-blue-500/30"
      />
      <CountdownTimer
        targetDate={chipDeadline}
        label="Chip Activation Deadline"
        icon={Zap}
        colorClass="text-purple-400"
        bgClass="bg-purple-500/10"
        borderClass="border-purple-500/30"
      />
    </div>
  );
};

export default CountdownTimer;
